import { ArrowsUpDownIcon, ArrowTrendingUpIcon } from '@heroicons/react/24/solid';
import { useNavigate } from '@tanstack/react-router';
import { useLocalStorage } from 'usehooks-ts';
import { ASSET_ROUTE_URL } from '../../../constants/routes';
import { ProductType } from '../../../hooks/useProducts';
import { localStorageKeys } from '../../../lib/localstorageKeys';
import { Button } from '../../catalyst-ui-kit/button';
import { InlineSelect } from '../../InlineSelect/InlineSelect';
import { PersistentBottomBar } from '../../PersistentBottomBar/PersistentBottomBar';
import { TradeFormPrimaryButton } from '../TradeForm/TradeFormPrimaryButton';
import { useTradeFormStore } from '../TradeForm/TradeFormState';

type MobileViewSwitcherProps = {
  product: ProductType;
  tab: 'chart' | 'trade';
  onTabChange: (tab: 'chart' | 'trade') => void;
};

const MOBILE_VIEW_OPTIONS = [
  {
    value: 'chart' as const,
    label: 'Chart',
    display: (
      <div className="flex flex-col items-center gap-1">
        <ArrowTrendingUpIcon className="size-4" />
        <span className="text-xs">Chart</span>
      </div>
    ),
  },
  {
    value: 'trade' as const,
    label: 'Trade',
    display: (
      <div className="flex flex-col items-center gap-1">
        <ArrowsUpDownIcon className="size-4" />
        <span className="text-xs">Trade</span>
      </div>
    ),
  },
];

export function MobileViewSwitcher({ tab, product, onTabChange }: MobileViewSwitcherProps) {
  const setSide = useTradeFormStore((x) => x.setSide);
  const navigate = useNavigate({ from: ASSET_ROUTE_URL });
  const [ignoreMobileOverlay] = useLocalStorage(localStorageKeys.dismissMobileWarning, false);

  if (!ignoreMobileOverlay) {
    return null;
  }

  return (
    <PersistentBottomBar className="border-t border-borderPrimary">
      <div className="flex items-center gap-2 px-3 py-2">
        <InlineSelect
          options={MOBILE_VIEW_OPTIONS}
          selected={tab}
          onChange={(option) => option && onTabChange(option.value)}
          className="min-w-[40%] justify-center"
          buttonClassName="flex-1 !p-0"
          activeClassName="bg-backgroundPrimary text-contentPrimary"
        />
        <div className="flex flex-1 gap-2">
          {tab === 'chart' ? (
            <>
              <Button
                color="green"
                className="flex-1"
                onClick={() => {
                  setSide('long', () => {
                    void navigate({ search: (prev) => ({ ...prev, side: 'long' }) });
                  });
                  onTabChange('trade');
                }}
              >
                Long
              </Button>
              <Button
                color="red"
                className="flex-1"
                onClick={() => {
                  setSide('short', () => {
                    void navigate({ search: (prev) => ({ ...prev, side: 'short' }) });
                  });
                  onTabChange('trade');
                }}
              >
                Short
              </Button>
            </>
          ) : (
            <TradeFormPrimaryButton product={product} />
          )}
        </div>
      </div>
    </PersistentBottomBar>
  );
}
